/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Cell } from "react-table";
import { SVGIcon, SVGIconProps, Text, FlexContainer } from "@nordcloud/gnui";
import { CostCategoryMapping, Provider } from "~/generated/graphql";
import { ButtonWrap } from "~/components";
import { TableExpander } from "~/components/Tables/Columns/Expander";
import { getProviderIconName } from "~/tools";
import { options } from "./CostSplittingDefs";
import { ButtonCss } from "./styles";
import { CostMappingAlgorithm } from "./types";

type Row = {
  id: string;
  costType: string;
  type: string;
  icon: SVGIconProps["name"];
  subRows: CostCategoryMapping[];
  provider: Provider;
  settings?: {
    allEnvironments: boolean;
    environments: string[];
  };
  algorithm: CostMappingAlgorithm;
};

type Methods = {
  onChangeAlgorithm: (id: string) => React.MouseEventHandler<HTMLButtonElement>;
  onChangeEnvs: (
    id: string,
    rowId: string,
    costType: string
  ) => React.MouseEventHandler<HTMLButtonElement>;
};

export const costSplittingRulesColumns = ({
  onChangeAlgorithm,
  onChangeEnvs,
}: Methods) => [
  ...TableExpander(),
  {
    Header: "Cost Type",
    accessor: "costType",
    disableSortBy: true,
    width: "18rem",
    Cell: ({ row }: Cell<Row>) => {
      return (
        <FlexContainer {...row.getToggleRowExpandedProps()}>
          <SVGIcon name={row.original.icon} />
          <Text css={{ marginLeft: "0.6rem" }} tag="div" weight="bold">
            {row.original.costType}
          </Text>
        </FlexContainer>
      );
    },
  },
  {
    Header: "Provider",
    accessor: "provider",
    disableSortBy: true,
    hasLeftBorder: true,
    width: "3%",
    Cell: ({ row }: Cell<Row>) =>
      (row?.original?.provider && (
        <FlexContainer justifyContent="center">
          <SVGIcon
            name={getProviderIconName(row.original.provider)}
            size="lg"
          />
        </FlexContainer>
      )) ||
      "",
  },
  {
    Header: "Assigning Method",
    accessor: "algorithm",
    disableSortBy: true,
    Cell: ({ row }: Cell<Row>) =>
      (row.original?.settings && (
        <ButtonWrap
          css={ButtonCss}
          onClick={onChangeAlgorithm(row.original.id)}
        >
          {options.get(row.original.algorithm)
            ? options.get(row.original.algorithm)?.label
            : row.original.algorithm}
        </ButtonWrap>
      )) ||
      "",
  },
  {
    Header: "Assigned To",
    accessor: "assignedTo",
    disableSortBy: true,
    Cell: ({ row }: Cell<Row>) =>
      (row.original?.settings && (
        <ButtonWrap
          css={ButtonCss}
          onClick={onChangeEnvs(
            row?.original?.id,
            row?.id.slice(0, 1),
            row?.original?.type ?? ""
          )}
        >
          {row.original?.settings?.allEnvironments === true &&
            "All environments"}
          {!row.original?.settings?.allEnvironments && (
            <>
              Selected environments (
              {row?.original?.settings?.environments?.length})
            </>
          )}
        </ButtonWrap>
      )) ||
      "",
  },
];
